// Admin routes for P5-B (STORE, THEME, CONTENT). Owned by that worker; the router mounts whatever this returns under the
// app shell (before the store-area placeholder), so modules never edit router.tsx.
import { useEffect } from "react";
import {
  createRoute,
  useNavigate,
  useParams,
  type AnyRoute,
  lazyRouteComponent,
} from "@tanstack/react-router";

// Pages are code-split: each loads when its route opens, not with the shell.
const load1 = () => import("./storefront/CodeEditorPage");
const CodeEditorPage = lazyRouteComponent(load1, "CodeEditorPage");
const CodePreviewPage = lazyRouteComponent(load1, "CodePreviewPage");
const load2 = () => import("./storefront/ContentPage");
const ContentPage = lazyRouteComponent(load2, "ContentPage");
const load3 = () => import("./storefront/InboxPage");
const InboxPage = lazyRouteComponent(load3, "InboxPage");
const load4 = () => import("./storefront/MenusPage");
const MenusPage = lazyRouteComponent(load4, "MenusPage");
const load5 = () => import("./storefront/MiscPages");
const DomainsPage = lazyRouteComponent(load5, "DomainsPage");
const load6 = () => import("./storefront/MoveStorePage");
const MoveStorePage = lazyRouteComponent(load6, "MoveStorePage");
const load7 = () => import("./storefront/PageEditorPage");
const PageEditorPage = lazyRouteComponent(load7, "PageEditorPage");
const load8 = () => import("./storefront/PoliciesPage");
const PoliciesPage = lazyRouteComponent(load8, "PoliciesPage");
const load9 = () => import("./storefront/SeoPage");
const SeoPage = lazyRouteComponent(load9, "SeoPage");
const load10 = () => import("./storefront/StoreInfoPages");
const BranchesPage = lazyRouteComponent(load10, "BranchesPage");
const SellingPointsPage = lazyRouteComponent(load10, "SellingPointsPage");
const load11 = () => import("./storefront/ThemePage");
const ThemePage = lazyRouteComponent(load11, "ThemePage");
const ThemePreviewPage = lazyRouteComponent(load11, "ThemePreviewPage");
const TemplatesPage = lazyRouteComponent(load11, "TemplatesPage");

const PAGES = [
  ["store/theme", ThemePage],
  // Owner, 2026-11-01: the current theme and the theme gallery (sidebar "Templates").
  ["store/templates", TemplatesPage],
  ["store/content", ContentPage],
  ["store/page-editor", PageEditorPage],
  ["store/code-editor", CodeEditorPage],
  // The code editor's Preview tab (owner, 2026-10-26); opened with ?window=ide.
  ["store/code-preview", CodePreviewPage],
  // D-091: a theme's sample store, opened from "Preview theme" in a new tab with ?window=ide&theme=.
  ["store/theme-preview", ThemePreviewPage],
  ["store/policies", PoliciesPage],
  ["store/menus", MenusPage],
  ["store/domains", DomainsPage],
  ["store/seo", SeoPage],
  ["store/move", MoveStorePage],
  ["store/branches", BranchesPage],
  ["store/selling-points", SellingPointsPage],
  ["customers/inbox", InboxPage],
] as const;

/** Merged cards (owner, 2026-10-22): the old address opens the tab that replaced it. The theme
 * library has its own page, Templates, since 2026-11-01 (owner). */
const MOVED = [
  ["store/theme-library", "store/templates", null],
  ["store/blog", "store/content", "blog"],
  // Featured sections moved into the theme editor's "Add a section" (owner, 2026-10-27).
  ["store/featured", "store/theme", "home"],
] as const;

function moved(to: string, tab: string | null) {
  return function Moved() {
    const { org, store } = useParams({ strict: false }) as { org: string; store: string };
    const navigate = useNavigate();
    useEffect(() => {
      void navigate({
        to: `/${org}/${store}/${to}`,
        ...(tab ? { search: { tab } as never } : {}),
        replace: true,
      });
    }, [navigate, org, store]);
    return null;
  };
}

export function storefrontRoutes(appRoute: AnyRoute): AnyRoute[] {
  return [
    ...PAGES.map(([path, component]) =>
      createRoute({ getParentRoute: () => appRoute, path: `/$org/$store/${path}`, component }),
    ),
    ...MOVED.map(([from, to, tab]) =>
      createRoute({
        getParentRoute: () => appRoute,
        path: `/$org/$store/${from}`,
        component: moved(to, tab),
      }),
    ),
  ];
}
